// super-excalidraw: thin client for the server-side animation endpoints
// (/api/v2/{id}/animation/*). The Go server decrypts the scene, the Node
// worker renders it (Playwright + ffmpeg), and the result is stored in MinIO.

// Per-element entrance animation style. "draw" = excalidraw-animate's
// handwriting/stroke trace (the original library feel); the rest are
// synthesised by the render worker from the SVG.
export type AnimType =
  | "draw"
  | "typewriter"
  | "fade"
  | "zoom"
  | "wipe"
  | "appear";

export const ANIM_TYPES: { value: AnimType; label: string }[] = [
  { value: "draw", label: "Draw-on (handwrite)" },
  { value: "typewriter", label: "Typewriter" },
  { value: "fade", label: "Fade in" },
  { value: "zoom", label: "Zoom / Pop" },
  { value: "wipe", label: "Wipe (L→R)" },
  { value: "appear", label: "Appear" },
];

export type AnimWindow = { id: string; start: number; duration: number; type?: AnimType };
export type AnimSpec = {
  // Optional: render only this frame's children, sized to the frame's bounds
  // (so the video orientation follows the frame).
  frameId?: string;
  // Default entrance style for elements without an explicit `type`.
  defaultType?: AnimType;
  // Per-element timing windows (start + duration in ms). Order = start times.
  elements?: AnimWindow[];
  // Magic-Move slideshow: render the frames as a morphing deck (each element's
  // customData.morphId/enter/loop drives it). Overrides the entrance fields.
  presentation?: {
    frames?: string[];
    holdMs?: number;
    morphMs?: number;
    // "morph" (default): Magic-Move — elements morph in place between slides.
    // "pan": the camera travels frame-to-frame across the whole canvas, so
    // inter-frame connector arrows are visible in the video.
    mode?: "morph" | "pan";
  };
};

export type AnimFormat = "mp4" | "gif" | "webm";
export type AnimStatus = {
  status: "queued" | "rendering" | "done" | "error";
  format?: string;
  url?: string;
  error?: string;
  updated?: number;
};

const base = (id: string) => `/api/v2/${encodeURIComponent(id)}/animation`;

export async function renderAnimation(
  id: string,
  opts: { spec?: AnimSpec; format?: AnimFormat; resolution?: string },
): Promise<{ jobId: string; status: string }> {
  const r = await fetch(`${base(id)}/render`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      spec: opts.spec ?? {},
      format: opts.format ?? "mp4",
      resolution: opts.resolution ?? "",
    }),
  });
  if (!r.ok) {
    throw new Error(`render failed: ${r.status} ${await r.text()}`);
  }
  return r.json();
}

export async function getAnimStatus(id: string, jobId: string): Promise<AnimStatus> {
  const r = await fetch(`${base(id)}/status?job=${encodeURIComponent(jobId)}`);
  if (!r.ok) {
    throw new Error(`status failed: ${r.status}`);
  }
  return r.json();
}

export async function getAnimSpec(id: string): Promise<AnimSpec> {
  const r = await fetch(`${base(id)}/spec`);
  if (!r.ok) {
    return {};
  }
  return r.json();
}

export async function putAnimSpec(id: string, spec: AnimSpec): Promise<void> {
  const r = await fetch(`${base(id)}/spec`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(spec),
  });
  if (!r.ok) {
    throw new Error(`spec save failed: ${r.status}`);
  }
}

export function animResultUrl(id: string, format: AnimFormat): string {
  return `${base(id)}/result?fmt=${format}`;
}

// Delete a rendered video from the server (so it leaves the renders list).
export async function deleteAnimation(
  id: string,
  format: AnimFormat,
): Promise<void> {
  const r = await fetch(`${base(id)}/result?fmt=${format}`, {
    method: "DELETE",
  });
  if (!r.ok && r.status !== 404) {
    throw new Error(`delete failed: ${r.status}`);
  }
}

// Poll until the job finishes (or errors / times out). onTick gets each status.
// Pass signal to cancel polling without aborting the backend render — returns null
// when aborted so callers can distinguish "suspended" from "error".
export async function waitForAnimation(
  id: string,
  jobId: string,
  onTick?: (s: AnimStatus) => void,
  timeoutMs = 8 * 60 * 1000,
  signal?: AbortSignal,
): Promise<AnimStatus | null> {
  const start = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (signal?.aborted) return null;
    const s = await getAnimStatus(id, jobId);
    if (signal?.aborted) return null;
    onTick?.(s);
    if (s.status === "done" || s.status === "error") {
      return s;
    }
    if (Date.now() - start > timeoutMs) {
      return { status: "error", error: "render timed out" };
    }
    // Interruptible sleep: resolves immediately when signal fires.
    await new Promise<void>((res) => {
      const t = setTimeout(res, 1500);
      signal?.addEventListener("abort", () => { clearTimeout(t); res(); }, { once: true });
    });
  }
}
